import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import { forgotPassword, resetPassword } from '../auth'
import { Field, PASSWORD_HINT } from '../components/AuthParts'
import { ProblemBanner } from '../components/Problem'

/** Reset a forgotten password: ask for an emailed code, then set a new password with it. */
export default function ForgotPassword() {
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [asked, setAsked] = useState(false)
  const [code, setCode] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [error, setError] = useState<unknown>(null)
  const [busy, setBusy] = useState(false)

  const run = (work: Promise<void>) => {
    setBusy(true)
    setError(null)
    work.catch(setError).finally(() => setBusy(false))
  }

  if (!asked) {
    return (
      <div className="auth">
        <form
          className="auth-form"
          onSubmit={(event) => {
            event.preventDefault()
            run(forgotPassword(email.trim()).then(() => setAsked(true)))
          }}
        >
          <h1>Reset your password</h1>
          <p>Enter your email address and we will send you a code.</p>
          <ProblemBanner error={error} />
          <Field
            label="Email"
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            autoComplete="username"
            required
            autoFocus
          />
          <div className="row">
            <button className="primary" type="submit" disabled={busy}>
              {busy ? 'Sending…' : 'Send the code'}
            </button>
          </div>
          <p className="muted">
            <Link to="/login">Back to log in</Link>
          </p>
        </form>
      </div>
    )
  }

  return (
    <div className="auth">
      <form
        className="auth-form"
        onSubmit={(event) => {
          event.preventDefault()
          run(
            resetPassword(email.trim(), code.trim(), newPassword).then(() =>
              navigate('/login?reset=1', { replace: true }),
            ),
          )
        }}
      >
        <h1>Choose a new password</h1>
        <p>
          If there is an account for <strong>{email.trim()}</strong>, we have emailed it a code.
        </p>
        <ProblemBanner error={error} />
        <Field
          label="Code from the email"
          value={code}
          onChange={(event) => setCode(event.target.value)}
          inputMode="numeric"
          autoComplete="one-time-code"
          required
          autoFocus
        />
        <Field
          label="New password"
          type="password"
          value={newPassword}
          onChange={(event) => setNewPassword(event.target.value)}
          autoComplete="new-password"
          hint={PASSWORD_HINT}
          required
        />
        <div className="row">
          <button className="primary" type="submit" disabled={busy}>
            {busy ? 'Saving…' : 'Change password'}
          </button>
        </div>
      </form>
    </div>
  )
}
